import JSZip from "jszip";
import { Download, FolderPlus, RefreshCw, Save } from "lucide-react";
import * as React from "react";
import { saveAs } from "file-saver";

import { AIChatPanel } from "@/components/copilot/ai-chat";
import { CodeEditor } from "@/components/copilot/code-editor";
import { FileExplorer } from "@/components/copilot/file-explorer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  createWorkspace,
  deleteFile,
  listFiles,
  readFile,
  writeFile,
  type Workspace,
  type WorkspaceFile,
} from "@/services/workspace";

const WORKSPACE_ID_KEY = "debug-pavilion:workspace-id";
const WORKSPACE_NAME_KEY = "debug-pavilion:workspace-name";

export function CopilotPage() {
  const [workspace, setWorkspace] = React.useState<Workspace | null>(null);
  const [files, setFiles] = React.useState<WorkspaceFile[]>([]);
  const [selectedPath, setSelectedPath] = React.useState<string | null>(null);
  const [content, setContent] = React.useState<string>("");
  const [workspaceName, setWorkspaceName] = React.useState("default");

  const loadFiles = React.useCallback(async () => {
    if (!workspace) return;
    const data = await listFiles(workspace.id);
    setFiles(data);
  }, [workspace]);

  React.useEffect(() => {
    const storedId = localStorage.getItem(WORKSPACE_ID_KEY);
    const storedName = localStorage.getItem(WORKSPACE_NAME_KEY);
    if (storedId && storedName) {
      setWorkspace({ id: storedId, name: storedName, path: "", created_at: 0 });
      setWorkspaceName(storedName);
    } else {
      void initWorkspace();
    }

    async function initWorkspace() {
      const ws = await createWorkspace("default");
      setWorkspace(ws);
      setWorkspaceName(ws.name);
      localStorage.setItem(WORKSPACE_ID_KEY, ws.id);
      localStorage.setItem(WORKSPACE_NAME_KEY, ws.name);
    }
  }, []);

  React.useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  const handleSelect = async (path: string) => {
    if (!workspace) return;
    setSelectedPath(path);
    try {
      const data = await readFile(workspace.id, path);
      setContent(data);
    } catch (error) {
      setContent(`// 读取失败：${String(error)}`);
    }
  };

  const handleCreate = async (path: string) => {
    if (!workspace) return;
    await writeFile(workspace.id, path, "");
    await loadFiles();
    await handleSelect(path);
  };

  const handleDelete = async (path: string) => {
    if (!workspace) return;
    await deleteFile(workspace.id, path);
    if (selectedPath === path) {
      setSelectedPath(null);
      setContent("");
    }
    await loadFiles();
  };

  const handleSave = async () => {
    if (!workspace || !selectedPath) return;
    await writeFile(workspace.id, selectedPath, content);
    await loadFiles();
  };

  const handleCreateWorkspace = async () => {
    const ws = await createWorkspace(workspaceName);
    setWorkspace(ws);
    localStorage.setItem(WORKSPACE_ID_KEY, ws.id);
    localStorage.setItem(WORKSPACE_NAME_KEY, ws.name);
    setFiles([]);
    setSelectedPath(null);
    setContent("");
  };

  const handleExportZip = async () => {
    if (!workspace) return;
    const zip = new JSZip();
    const allFiles = await listFiles(workspace.id);
    for (const file of allFiles) {
      const data = await readFile(workspace.id, file.path);
      zip.file(file.path, data);
    }
    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, `${workspace.name}.zip`);
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI 副驾驶</h1>
          <p className="text-sm text-muted-foreground">工作区文件管理 + Monaco 编辑器 + AI 对话。</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            className="h-8 w-40 text-sm"
            placeholder="工作区名称"
          />
          <Button variant="outline" size="sm" onClick={handleCreateWorkspace}>
            <FolderPlus className="mr-1.5 h-4 w-4" />
            新建工作区
          </Button>
          <Button variant="outline" size="sm" onClick={() => void loadFiles()}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            刷新
          </Button>
          <Button variant="outline" size="sm" onClick={() => void handleExportZip()}>
            <Download className="mr-1.5 h-4 w-4" />
            导出 zip
          </Button>
          <Button size="sm" disabled={!selectedPath} onClick={handleSave}>
            <Save className="mr-1.5 h-4 w-4" />
            保存
          </Button>
        </div>
      </div>

      <div className="flex flex-1 gap-3 overflow-hidden">
        <div className="w-56 flex-shrink-0 overflow-hidden rounded-lg border">
          <FileExplorer
            files={files}
            selectedPath={selectedPath}
            onSelect={handleSelect}
            onCreate={handleCreate}
            onDelete={handleDelete}
          />
        </div>
        <div className="flex flex-1 flex-col overflow-hidden rounded-lg border bg-card">
          {selectedPath ? (
            <CodeEditor
              path={selectedPath}
              value={content}
              onChange={setContent}
              onSave={handleSave}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              选择或创建一个文件开始编辑
            </div>
          )}
        </div>
        <div className="w-96 flex-shrink-0 overflow-hidden rounded-lg border">
          <AIChatPanel
            context={selectedPath ? content : undefined}
            onApplyCode={(code) => {
              setContent(code);
              void handleSave();
            }}
          />
        </div>
      </div>
    </div>
  );
}
